<template>
  <div class="circle-property">
    <n-collapse :default-expanded-names="defaultExpanded" class="pm-collapse">
      <n-collapse-item title="圆类型" name="type">
        <div class="circle-type-grid">
          <button
            type="button"
            class="circle-type-button"
            :class="{ active: options.mode === SketchCircleCommand.CenterRadius }"
            :disabled="!canEdit"
            @click="handleModeChange(SketchCircleCommand.CenterRadius)"
          >
            <SvgIcon :content="circleCenterIcon" alt="圆(R)" />
          </button>
          <button
            type="button"
            class="circle-type-button"
            :class="{ active: options.mode === SketchCircleCommand.ThreePoint }"
            :disabled="!canEdit"
            @click="handleModeChange(SketchCircleCommand.ThreePoint)"
          >
            <SvgIcon :content="circleThreePointIcon" alt="周边圆" />
          </button>
        </div>
      </n-collapse-item>

      <n-collapse-item v-if="showAdvancedSections" title="现有几何关系" name="relations">
        <SelectionListFrame
          :items="existingRelations"
          selection-behavior="ctrl-multiple"
          list-role="list"
          :visible-rows="4"
          context-menu-enabled
          :is-item-delete-enabled="isSketchRelationSnapshotDeleteEnabled"
          :show-delete-all-action="existingRelations.some(isSketchRelationSnapshotDeleteEnabled)"
          item-key-field="relationId"
          @delete-item="handleDeleteRelationSelection"
          @delete-all="handleDeleteAllRelations"
        >
          <template #item="{ item }">
            <SketchRelationDiagnosticText
              :item="item"
              :label="resolveRelationLabel(item)"
            />
          </template>
        </SelectionListFrame>
      </n-collapse-item>

      <n-collapse-item v-if="showAdvancedSections" title="添加几何关系" name="add-relations">
        <div class="add-relations-section">
          <n-space size="small" wrap>
            <n-button
              v-for="relation in availableRelations"
              :key="relation.relationType"
              size="small"
              quaternary
              :disabled="relation.disabled"
              @click="handleAddRelation(relation.relationType)"
            >
              {{ relation.label }}
            </n-button>
          </n-space>
        </div>
      </n-collapse-item>

      <n-collapse-item v-if="showAdvancedSections" title="选项" name="options">
        <n-checkbox
          :checked="constructionChecked"
          :disabled="!canEdit || !isCircleSelected"
          @update:checked="handleForConstruction"
        >
          作为构造线
        </n-checkbox>
      </n-collapse-item>

      <n-collapse-item title="参数" name="parameters">
        <div class="param-grid">
          <div class="param-row">
            <n-text depth="3" class="param-label">X</n-text>
            <n-input-number
              :value="center2d?.[0] ?? null"
              size="small"
              :disabled="!canEditParameters"
              :show-button="true"
              :precision="8"
              :step="1"
              @update:value="(value) => handleCenterInput('x', value)"
            />
          </div>
          <div class="param-row">
            <n-text depth="3" class="param-label">Y</n-text>
            <n-input-number
              :value="center2d?.[1] ?? null"
              size="small"
              :disabled="!canEditParameters"
              :show-button="true"
              :precision="8"
              :step="1"
              @update:value="(value) => handleCenterInput('y', value)"
            />
          </div>
          <div class="param-row">
            <n-text depth="3" class="param-label">R</n-text>
            <n-input-number
              :value="radius ?? null"
              size="small"
              :disabled="!canEditParameters"
              :show-button="true"
              :precision="8"
              :step="1"
              @update:value="handleRadiusInput"
            />
          </div>
        </div>
      </n-collapse-item>
    </n-collapse>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeUnmount } from 'vue';
import {
  NButton,
  NCheckbox,
  NCollapse,
  NCollapseItem,
  NInputNumber,
  NSpace,
  NText,
} from 'naive-ui';
import type { CircleOptions, CircleParameters, CircleParameterUpdate, PropertyManagerContext } from '@/core';
import { useViewer } from '@/hooks';
import { useGetSelectCircle } from '@/hooks';
import SelectionListFrame from '@/components/editor/SelectionListFrame.vue';
import SketchRelationDiagnosticText from '../common/SketchRelationDiagnosticText.vue';
import {isSketchRelationSnapshotDeleteEnabled} from '@/utils/sketch/isSketchRelationSnapshotDeleteEnabled';

import {
  type AvailableRelationButtonViewModel,
  roundToEightDecimalPlaces,
  SketchCircleCommand,
  type SketchRelationKind,
  type SketchRelationSnapshot,
  
  convertMmToM,
  convertMToMm,
  
} from '@puppycad/cad-core';
import SvgIcon from '@/components/common/SvgIcon.vue';
import circleCenterIcon from '@/assets/icon/circle_center.svg?raw';
import circleThreePointIcon from '@/assets/icon/circle_3point.svg?raw';

defineOptions({
  inheritAttrs: false,
});

interface CirclePropertySectionProps {
  /** 属性面板上下文，用于把草图属性编辑统一收口到 cad-core。 */
  context?: PropertyManagerContext;
}

const props = defineProps<CirclePropertySectionProps>();

const defaultOptions: CircleOptions = {
  mode: SketchCircleCommand.CenterRadius,
  forConstruction: false,
};


const { getViewer } = useViewer();
const { getSelected: getSelectedCircle, onChanged: onCircleChanged, onCircleParametersChanged, onDrawingPreviewChanged } = useGetSelectCircle();

const localCircleOptions = ref<CircleOptions>({ ...defaultOptions });
const localCircleParameters = ref<CircleParameters | null>(null);
/** “作为构造线”勾选状态镜像，避免直接依赖非响应式草图对象字段。 */
const constructionCheckedValue = ref(false);

/** 是否有圆被选中。 */
const isCircleSelected = ref(false);
/** 当前选中圆的现有关系快照列表。 */
const existingRelations = ref<SketchRelationSnapshot[]>([]);
/** 当前选中圆可创建的几何关系按钮列表。 */
const availableRelations = ref<AvailableRelationButtonViewModel[]>([]);

/**
 * 同步“作为构造线”勾选状态。
 *
 * 设计说明：
 * 选中的圆是 cad-core 运行时对象，不允许放入 Vue 响应式状态。
 * 因此面板只保存 boolean 镜像，并在选择变化或用户操作时显式同步。
 *
 * @param circle 当前选中的圆对象。
 */
function syncConstructionCheckedState(circle: ReturnType<typeof getSelectedCircle>): void {
  if (circle) {
    constructionCheckedValue.value = Boolean(circle.isConstruction);
    return;
  }
  constructionCheckedValue.value = Boolean(localCircleOptions.value.forConstruction);
}

const circleChangedSub = onCircleChanged().subscribe((circle) => {
  isCircleSelected.value = circle !== null;
  refreshPanelState(circle);
  syncConstructionCheckedState(circle);
  syncCircleParameters(circle);
});


/** 是否处于绘制预览状态。 */
const isDrawingPreview = ref(false);
const circleParamsSub = onCircleParametersChanged().subscribe((params) => {
  // 仅在有值时更新，null 由 circleChangedSub / syncCircleParameters 管理。
  if (params) {
    localCircleParameters.value = params;
  }
});
const drawingPreviewSub = onDrawingPreviewChanged().subscribe((drawing) => {
  isDrawingPreview.value = drawing;
});


onBeforeUnmount(() => {
  circleChangedSub.unsubscribe();
  circleParamsSub.unsubscribe();
  drawingPreviewSub.unsubscribe();
});
const canEdit = computed(() => Boolean(getViewer()));
const canEditParameters = computed(() => Boolean(isCircleSelected.value && !isDrawingPreview.value && getViewer()));
const showAdvancedSections = computed(() => isCircleSelected.value);
const options = computed(() => localCircleOptions.value);

const center2d = computed(() => {
  const c = localCircleParameters.value?.center2d ?? null;
  if (!c) return null;
  return [
    roundToEightDecimalPlaces(convertMToMm(c[0])),
    roundToEightDecimalPlaces(convertMToMm(c[1])),
  ];
});
const radius = computed(() => {
  const r = localCircleParameters.value?.radius ?? null;
  if (r === null) return null;
  return roundToEightDecimalPlaces(convertMToMm(r));
});

const constructionChecked = computed(() => constructionCheckedValue.value);

const defaultExpanded = ['type', 'parameters', 'options'];

function updateOptions(patch: Partial<CircleOptions>): void {
  const renderer = getViewer();
  const currentOptions = renderer?.getCircleDrawingOptions?.();
  if (!renderer || !currentOptions) {
    return;
  }
  renderer.setCircleDrawingOptions?.({ ...currentOptions, ...patch });
  localCircleOptions.value = { ...localCircleOptions.value, ...patch };
  syncConstructionCheckedState(getSelectedCircle());
}

function handleModeChange(mode: SketchCircleCommand): void {
  updateOptions({ mode });
}



/**
 * 刷新当前选中圆的可添加几何关系列表。
 * @param circle 选中的圆对象，传入 null 时清空按钮列表。
 */
function refreshAvailableRelations(circle: ReturnType<typeof getSelectedCircle>): void {
  if (!circle) {
    availableRelations.value = [];
    return;
  }

  // 可创建关系由 cad-core 统一计算，避免属性面板重复实现关系兼容判断。
  availableRelations.value = getViewer()?.sketchRelationCreationService.build(circle) ?? [];
}

/**
 * 刷新圆属性面板中的几何关系状态。
 * @param circle 选中的圆对象，传入 null 时清空关系展示状态。
 */
function refreshPanelState(circle: ReturnType<typeof getSelectedCircle>): void {
  refreshExistingRelations(circle);
  refreshAvailableRelations(circle);
}

/**
 * 刷新当前选中圆的现有关系列表。
 * @param circle 选中的圆对象（null 表示无选中）。
 */
function refreshExistingRelations(circle: ReturnType<typeof getSelectedCircle>): void {
  if (!circle) {
    existingRelations.value = [];
    return;
  }
  existingRelations.value = circle.constraints.getRelationSnapshots();
}

/**
 * 将关系快照转为显示标签。
 * @param item 列表项。
 * @returns 显示标签。
 */
function resolveRelationLabel(item: unknown): string {
  const snapshot = item as SketchRelationSnapshot | null;
  if (!snapshot) {
    return '';
  }
  return snapshot.baseDisplayName || snapshot.name || String(snapshot.relationType);
}

async function removeCircleRelation(
  circle: NonNullable<ReturnType<typeof getSelectedCircle>>,
  relationId: number,
): Promise<boolean> {
  return await props.context?.removeSketchRelation?.(circle, relationId) ?? false;
}

/**
 * 处理右键删除单个关系。
 * @param payload SelectionListFrame delete-item 回调参数。
 */
async function handleDeleteRelationSelection(payload: { item: unknown; index: number }): Promise<void> {
  const circle = getSelectedCircle();
  if (!circle) {
    return;
  }
  const snapshot = payload.item as SketchRelationSnapshot;
  if (!snapshot || !Number.isFinite(snapshot.id) || !isSketchRelationSnapshotDeleteEnabled(snapshot)) {
    return;
  }
  if (!await removeCircleRelation(circle, snapshot.id)) {
    return;
  }
  const viewer = getViewer();
  viewer?.removeObjectConstraintMarker?.(circle, snapshot.id);
  refreshPanelState(circle);
}

/**
 * 处理右键删除全部关系。
 */
async function handleDeleteAllRelations(): Promise<void> {
  const circle = getSelectedCircle();
  if (!circle) {
    return;
  }
  const viewer = getViewer();
  for (const snapshot of existingRelations.value.filter(isSketchRelationSnapshotDeleteEnabled)) {
    if (!await removeCircleRelation(circle, snapshot.id)) {
      return;
    }
    viewer?.removeObjectConstraintMarker?.(circle, snapshot.id);
  }
  refreshPanelState(circle);
}




/**
 * 处理圆属性面板中的添加几何关系操作。
 * @param relationType 需要创建的几何关系类型。
 */
function handleAddRelation(relationType: SketchRelationKind): void {
  const selectedCircle = getSelectedCircle();
  const viewer = getViewer();

  if (!selectedCircle || !viewer) {
    return;
  }

  // 创建动作必须走 cad-core 服务，确保按钮状态、约束绑定和首次求解路径一致。
  const created = viewer.sketchRelationCreationService.createRelation({
    objects: [selectedCircle],
    relationKind: relationType,
  });
  if (!created) {
    return;
  }

  refreshPanelState(selectedCircle);
}


function handleForConstruction(value: boolean): void {
  if (isCircleSelected.value) {
    const selectedCircle = getSelectedCircle();
    if (selectedCircle) {
      // 直接通过对象 setter 修改构造线状态（setter 内部切换材质）。
      prepareCirclePropertyEdit(selectedCircle);
      selectedCircle.isConstruction = value;
      const viewer = getViewer();
      viewer?.syncObjectBaseVisualState?.(selectedCircle);
      if (value || !viewer?.shadedSketchContoursEnabled) {
        selectedCircle.clearContourDisplay?.();
      } else {
        selectedCircle.syncContour?.();
      }
      constructionCheckedValue.value = value;
      finalizeCirclePropertyEdit(selectedCircle);
      syncConstructionCheckedState(selectedCircle);
      syncCircleParameters(selectedCircle);
      return;
    }
  }
  updateOptions({ forConstruction: value });
  constructionCheckedValue.value = value;
}

function handleCenterInput(axis: 'x' | 'y', value: number | null): void {
  if (value === null || Number.isNaN(value)) {
    return;
  }
  
  // 用户输入的毫米 (mm) 值转换为米 (m) 后存回
  updateSelectedCircle({ type: 'center', axis, value: convertMmToM(value) });
  
}

function handleRadiusInput(value: number | null): void {
  if (value === null || Number.isNaN(value)) {
    return;
  }
  
  // 用户输入的毫米 (mm) 值转换为米 (m) 后存回
  updateSelectedCircle({ type: 'radius', value: convertMmToM(value) });
  
}

/**
 * 从圆对象同步参数到本地状态。
 * @param circle 选中的圆对象（null 表示清空参数）。
 */
function syncCircleParameters(circle: ReturnType<typeof getSelectedCircle>): void {
  if (!circle) {
    localCircleParameters.value = null;
    return;
  }
  const centerLocal = circle.centerPoint;
  const centerWorld = circle.getCenterWorld();
  const currentRadius = circle.radius;
  const round = (value: number) => roundToEightDecimalPlaces(value);
  localCircleParameters.value = {
    center: centerWorld.toArray() as [number, number, number],
    center2d: [round(centerLocal.x), round(centerLocal.y)],
    radius: round(currentRadius),
  };
}

/**
 * 将圆属性编辑统一交给 PropertyManagerContext 收口。
 * 这样 setter 本身仍保持纯几何更新，是否提交草图定义由统一服务负责。
 * @param circle 被编辑的圆对象。
 * @param radiusTarget 本次半径输入必须保持的目标值（米）；其它属性编辑省略。
 */
/**
 * 在圆属性 setter 执行前捕获历史 before 快照。
 *
 * 说明：属性面板会直接修改圆对象，若等到 finalize 再捕获快照，
 * Undo 栈只能看到上一条绘制事务，导致第一次 Undo 直接删除圆。
 */
function prepareCirclePropertyEdit(circle: ReturnType<typeof getSelectedCircle>): void {
  if (!circle) {
    return;
  }
  props.context?.prepareSketchPropertyEdit?.(circle);
}

function finalizeCirclePropertyEdit(
  circle: ReturnType<typeof getSelectedCircle>,
  radiusTarget?: number,
): void {
  if (!circle) {
    return;
  }
  props.context?.finalizeSketchPropertyEdit?.(circle, {
    reason: 'property-edit',
    sourceObjectId: circle.uuid,
    ...(radiusTarget !== undefined ? { radiusTarget } : {}),
  });
}

function updateSelectedCircle(update: CircleParameterUpdate): void {
  const renderer = getViewer();
  const selectedCircle = getSelectedCircle();
  if (!renderer || !selectedCircle) {
    return;
  }

  prepareCirclePropertyEdit(selectedCircle);

  // 通过圆对象的 setter 直接修改圆心或半径，内部自动重建几何
  if (update.type === 'center') {
    const current = selectedCircle.centerPoint.clone();
    if (update.axis === 'x') {
      current.x = update.value;
    } else {
      current.y = update.value;
    }
    selectedCircle.centerPoint = current;
  } else if (update.type === 'radius') {
    selectedCircle.radius = update.value;
  }

  finalizeCirclePropertyEdit(
    selectedCircle,
    update.type === 'radius' ? update.value : undefined,
  );
  // 约束可能把输入值拉回到引用几何，必须在 finalize 后从对象回读。
  syncCircleParameters(selectedCircle);
}


/** 当前组件创建时已经选中的圆对象。 */
const initialSelectedCircle = getSelectedCircle();
isCircleSelected.value = initialSelectedCircle !== null;
refreshPanelState(initialSelectedCircle);
syncConstructionCheckedState(initialSelectedCircle);
syncCircleParameters(initialSelectedCircle);


</script>

<style scoped>
.circle-property {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pm-collapse :deep(.n-collapse-item__header) {
  padding: 6px 0;
}

.circle-type-grid {
  display: grid;
  grid-template-columns: repeat(2, 48px);
  gap: 8px;
}

.circle-type-button {
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.02);
  border-radius: 4px;
  cursor: pointer;
  padding: 0;
}

.circle-type-button :deep(.puppy-svg-icon) {
  width: 28px;
  height: 28px;
}

.circle-type-button.active {
  border-color: rgba(66, 153, 225, 0.8);
  box-shadow: 0 0 0 1px rgba(66, 153, 225, 0.4) inset;
}

.circle-type-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.add-relations-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.param-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.param-row {
  display: grid;
  grid-template-columns: 20px 1fr;
  gap: 8px;
  align-items: center;
}

.param-label {
  font-size: 12px;
}
</style>
